import { useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuthStore } from "@/store/authStore";
import { hasMinRole, type Role } from "@/lib/roles";
import { Loader2 } from "lucide-react";

interface AuthGuardProps {
  children: React.ReactNode;
  /**
   * Papel mínimo para entrar na rota. Ausente = basta estar autenticado.
   *
   * Substituiu o `adminOnly?: boolean`, que era código morto: existia desde que
   * foi escrito e **nenhuma rota o passava**. Virou `minRole` porque um booleano
   * só sabe perguntar por `admin`, e a hierarquia tem quatro degraus.
   *
   * ⚠️ Não é autorização — o papel vem do `/me` e pode estar velho por até 15
   * minutos. Quem autoriza é o servidor, em toda requisição. Isto evita montar uma
   * tela inteira que só vai disparar chamadas com `403`.
   */
  minRole?: Role;
}

export function AuthGuard({ children, minRole }: AuthGuardProps) {
  const { isAuthenticated, isLoading, loadUser, user } = useAuthStore();
  const location = useLocation();

  useEffect(() => {
    if (isLoading && !user) {
      loadUser();
    }
  }, [isLoading, user, loadUser]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0a1628]">
        <Loader2 className="h-8 w-8 animate-spin text-white/50" />
      </div>
    );
  }

  if (!isAuthenticated) {
    // `pathname + search`: quem for barrado numa URL com query voltava para a rota
    // sem ela depois de logar, e caía numa tela em outro estado. Nada de `hash` —
    // o navegador não o envia ao servidor e a aplicação não usa nenhum.
    const back = `${location.pathname}${location.search}`;
    return <Navigate to={`/login?redirect=${encodeURIComponent(back)}`} replace />;
  }

  // Destino `/projects`, não `/dashboard`: a rota antiga vai ser removida por
  // tarefa paralela (S-073 decisão 12), e um bloqueio que cai em `NotFound` é pior
  // que bloqueio nenhum — ele só acontece com quem **não** é admin, então quem
  // testa a aplicação como admin nunca o vê.
  if (minRole && !hasMinRole(user?.role, minRole)) {
    return <Navigate to="/projects" replace />;
  }

  return <>{children}</>;
}
